import { useRef, type FC, type ReactElement, type RefObject } from 'react';
import { graphql, useFragment } from 'react-relay';

import { ProductPrice } from './Details/ProductPrice.tsx';
import { ProductShippingWrapper } from './Details/ProductShipping.tsx';
import { ProductTitle } from './Details/ProductTitle.tsx';
import { ProductDetails } from './Details/ProductDetails.tsx';
import { ProductUrgencySignal } from './Details/ProductUrgencySignal.tsx';
import { QuickView } from './QuickView/QuickView.tsx';
import { QuickViewButton } from './QuickView/QuickViewButton.tsx';
import { useTileSizeClassNames } from './hooks/useTileSizeClassNames.ts';
import { useConditionalCallback } from 'dibs-react-hooks/exports/useConditionalCallback.ts';
import { trackEvent, eventNameConstants } from 'dibs-tracking';
import { SwatchSelectorMinimal } from 'dibs-swatch-selector/exports/SwatchSelectorMinimal.js';
import { ProductDimensions } from './Details/ProductDimensions.tsx';
import { ProductInternalTools } from './Details/ProductInternalTools.tsx';
import { useProductDimensions } from './hooks/useProductDimensions.ts';
import { TILE_SIZES, type OnClick, type TileSize } from './constants.ts';
import { IN, type MeasurementUnit } from 'dibs-intl/exports/units';

import { useVisibilityTracker } from 'dibs-visibility-tracker/exports/useVisibilityTracker.ts';

import classnames from 'classnames';
import dibsCss from 'dibs-css';
import styles from './styles/styles.scss';

import { type ProductBaseTile_viewer$key } from './__generated__/ProductBaseTile_viewer.graphql.ts';
import { type ProductBaseTile_item$key } from './__generated__/ProductBaseTile_item.graphql.ts';
import { type ProductBaseTile_user$key } from './__generated__/ProductBaseTile_user.graphql.ts';
import { type ProductBaseTile_itemSearch$key } from './__generated__/ProductBaseTile_itemSearch.graphql.ts';

const viewerFragment = graphql`
    fragment ProductBaseTile_viewer on Viewer {
        ...ProductShipping_viewer
    }
`;

const itemSearchFragment = graphql`
    fragment ProductBaseTile_itemSearch on ItemSearchQueryConnection {
        ...QuickView_itemSearch
        ...ProductInternalTools_itemSearch
    }
`;

const itemFragment = graphql`
    fragment ProductBaseTile_item on Item
    @argumentDefinitions(
        isTrade: { type: "Boolean", defaultValue: false }
        pageDisplayEnum: { type: "PageDisplayEnum", defaultValue: searchAndBrowse }
        priceBookName: { type: "String" }
        showSeller: { type: "Boolean!" }
    ) {
        localizedPdpUrl
        measurement {
            ...useProductDimensions_measurement
        }
        ...ProductTitle_item
        ...ProductPrice_item
            @arguments(
                isTrade: $isTrade
                pageDisplayEnum: $pageDisplayEnum
                priceBookName: $priceBookName
            )
        ...ProductShipping_item
        ...SwatchSelectorMinimal_item
        ...ProductDetails_item @arguments(showSeller: $showSeller)
        ...ProductInternalTools_item
        ...QuickView_item
    }
`;

const userFragment = graphql`
    fragment ProductBaseTile_user on User @argumentDefinitions(showSeller: { type: "Boolean!" }) {
        ...ProductDetails_user @arguments(showSeller: $showSeller)
        ...ProductInternalTools_user
    }
`;

const mskuSwatchDisplayCount = 3;

type Props = {
    viewer: ProductBaseTile_viewer$key | null | undefined;
    itemSearch: ProductBaseTile_itemSearch$key | null | undefined;
    item: ProductBaseTile_item$key | null | undefined;
    user: ProductBaseTile_user$key | null | undefined;
    afterDisplay: boolean;
    beforeRender?: () => void;
    currency: string;
    children: ReactElement;
    isSponsored?: boolean;
    isHovering: boolean;
    index: number;
    tileElementRef?: RefObject<HTMLDivElement | null>;
    hideRecentSoldPrice?: boolean;
    onClick?: OnClick;
    onContentVisible?: () => void;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    setIsHovering: (isHovering: boolean) => void;
    size: TileSize;
    showMskuSwatch?: boolean;
    showBrowseSimilar?: boolean;
    showMeasurements?: boolean;
    showPrice?: boolean;
    measurementUnit?: MeasurementUnit;
    urgencySignal?: string | null;
    shippingPrices?: Record<string, number> | null;
    showShippingCost?: boolean;
    hasQuickView?: boolean;
    showQuickViewButton?: boolean;
    onQuickViewDetailsClick?: () => void;
    favoritesComponent?: ReactElement | null;
    isShippingCostSbVariant?: boolean;
};

export const ProductBaseTile: FC<Props> = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
    item: itemRef,
    user: userRef,
    afterDisplay,
    beforeRender,
    currency,
    children,
    index,
    isHovering,
    isSponsored,
    tileElementRef,
    hideRecentSoldPrice,
    onClick,
    onContentVisible,
    onMouseEnter,
    onMouseLeave,
    setIsHovering,
    size,
    showMskuSwatch,
    showBrowseSimilar,
    showMeasurements = true,
    showPrice,
    showShippingCost,
    measurementUnit = IN,
    urgencySignal,
    shippingPrices,
    hasQuickView,
    showQuickViewButton,
    onQuickViewDetailsClick,
    favoritesComponent,
    isShippingCostSbVariant,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);

    const { tileSizeClassNames } = useTileSizeClassNames({ size });
    const defaultTileRef = useRef(null);
    const tileRef = tileElementRef || defaultTileRef;

    useConditionalCallback({
        condition: isHovering,
        cb: () =>
            trackEvent({
                eventName: eventNameConstants.EVENT_RESULT_INTERACTION,
                interaction_type: 'hover',
                step_interaction_name: 'product tile image',
            }),
        timeout: 500,
    });

    useVisibilityTracker({
        elementRef: tileRef,
        onVisibilityChange: ({ isVisible }) => {
            if (isVisible) {
                onContentVisible?.();
            }
        },
        isEnabled: !!onContentVisible,
        options: { threshold: 0.5 },
    });

    useVisibilityTracker({
        elementRef: tileRef,
        onVisibilityChange: ({ isVisible }) => {
            if (isVisible) {
                beforeRender?.();
            }
        },
        isEnabled: !!beforeRender,
        options: { rootMargin: '100% 0px' },
    });

    const { localizedPdpUrl, measurement } = item || {};
    const { dimensionValues, canShowDimensions } = useProductDimensions({
        measurement,
        showMeasurements,
        measurementUnit,
    });

    if (!item) {
        return null;
    }

    const isSmall = size === TILE_SIZES.small;

    return (
        <article
            className={classnames(
                styles.itemContainer,
                dibsCss.relative,
                dibsCss.boxBorder,
                dibsCss.flex,
                dibsCss.flexCol,
                dibsCss.wFull,
                dibsCss.minW0,
                tileSizeClassNames
            )}
            onMouseEnter={() => {
                setIsHovering(true);
                onMouseEnter?.();
            }}
            onMouseLeave={() => {
                setIsHovering(false);
                onMouseLeave?.();
            }}
            ref={tileRef}
            data-tn="item-tile-wrapper"
            data-tile-index={index}
        >
            {children}
            <div
                className={classnames(
                    dibsCss.wFull,
                    dibsCss.hFull,
                    dibsCss.mtXsmall,
                    dibsCss.relative,
                    dibsCss.textNoir
                )}
            >
                {isSmall && canShowDimensions && (
                    <p className={dibsCss.flex}>
                        <ProductDimensions
                            dimensionValues={dimensionValues}
                            className={dibsCss.textSatan}
                        />
                    </p>
                )}

                <header className={dibsCss.flex}>
                    <ProductTitle
                        item={item}
                        isSponsored={isSponsored}
                        onClick={onClick}
                        isSmall={isSmall}
                        canShowDimensions={canShowDimensions}
                        showMskuSwatch={showMskuSwatch}
                    />
                    {showQuickViewButton && <QuickViewButton index={index} />}
                </header>

                {showPrice ? (
                    <ProductPrice
                        currency={currency}
                        item={item}
                        isSmall={isSmall}
                        canShowDimensions={canShowDimensions}
                        hideRecentSoldPrice={hideRecentSoldPrice}
                    />
                ) : (
                    <p
                        className={classnames(
                            styles.pricePlaceHolder,
                            dibsCss.wFull,
                            dibsCss.mtXxsmall
                        )}
                    />
                )}
                {showShippingCost && (
                    <ProductShippingWrapper
                        viewer={viewer}
                        item={item}
                        currency={currency}
                        shippingPrices={shippingPrices}
                        isShippingCostSbVariant={isShippingCostSbVariant}
                    />
                )}
                <ProductUrgencySignal urgencySignal={urgencySignal} />
                {showMskuSwatch && (
                    <SwatchSelectorMinimal
                        displayCount={mskuSwatchDisplayCount}
                        item={item}
                        path={localizedPdpUrl}
                        onClick={onClick}
                        containerClassName={styles.mskuSwatchContainer}
                    />
                )}
                <ProductDetails
                    item={item}
                    user={user}
                    afterDisplay={afterDisplay}
                    isSponsored={isSponsored}
                    canShowDimensions={!isSmall && canShowDimensions}
                    dimensionValues={dimensionValues}
                    showBrowseSimilar={showBrowseSimilar}
                />
            </div>
            {hasQuickView && (
                <QuickView
                    itemSearch={itemSearch}
                    item={item}
                    index={index}
                    size={size}
                    onViewDetailsClick={onQuickViewDetailsClick}
                />
            )}
            {favoritesComponent}
            <ProductInternalTools item={item} user={user} itemSearch={itemSearch} size={size} />
        </article>
    );
};
